import { useEffect, useState } from "react"
import { Task, TaskStatus, TaskVersionSnapshot } from "@/schema"
import { DialogHeader } from "@ui/shad/dialog"

import { CustomDialog } from "@/components/dialogs"
import TaskDescription from "@/components/tasks/detail-slideover/task-description"
import TaskHeader from "@/components/tasks/detail-slideover/task-header"
import { TaskVersionHistory } from "@/components/tasks/task-version-history"

interface TaskVersionHistoryDialogProps {
  task: Task
  initialVersionId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onRestored: (task: Task) => void
}

export function TaskVersionHistoryDialog({
  task,
  initialVersionId,
  open,
  onOpenChange,
  onRestored,
}: TaskVersionHistoryDialogProps) {
  const [previewVersion, setPreviewVersion] = useState<TaskVersionSnapshot>()

  useEffect(() => {
    if (!open) setPreviewVersion(undefined)
  }, [open])

  const displayedTask = previewVersion
    ? { ...task, title: previewVersion.title, description: previewVersion.description }
    : task
  const isCompleted = task.status === TaskStatus.DONE || task.optimistic_status === TaskStatus.DONE

  function handleRestored(restoredTask: Task) {
    setPreviewVersion(undefined)
    onRestored(restoredTask)
    onOpenChange(false)
  }

  return (
    <CustomDialog
      customClassName="flex h-[min(48rem,calc(100dvh-3rem))] max-w-5xl flex-col gap-0 overflow-hidden p-0"
      title={"Version history"}
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogHeader>
        <div className="text-sm font-medium">{"Version history"}</div>
      </DialogHeader>
      <div className="flex min-h-0 min-w-0 flex-1">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-6">
          <TaskHeader
            key={`title-${task.id}-${previewVersion?.id ?? "current"}`}
            task={displayedTask}
            isCompleted={isCompleted}
            onComplete={() => undefined}
            readOnly
          />
          <TaskDescription
            key={`description-${task.id}-${previewVersion?.id ?? "current"}`}
            task={displayedTask}
            isCompleted={isCompleted}
            readOnly
          />
        </div>

        <aside className="bg-muted/30 w-80 shrink-0 overflow-y-auto border-l p-3">
          <TaskVersionHistory
            taskId={task.id}
            initialVersionId={initialVersionId}
            onPreview={setPreviewVersion}
            onRestored={handleRestored}
          />
        </aside>
      </div>
    </CustomDialog>
  )
}
